import { useEffect, useState } from 'react'
import { usePublicClient } from 'wagmi'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { getLogsChunked } from '@/lib/getLogsChunked'

function ev(abi, name) { return abi.find((x) => x.type === 'event' && x.name === name) }

const SUPPLIED = ev(poolAbi, 'Supplied')
const WITHDRAWN = ev(poolAbi, 'Withdrawn')
const BORROWED = ev(poolAbi, 'Borrowed')
const REPAID = ev(poolAbi, 'Repaid')
const SUPPLY_INCENTIVE_CLAIMED = ev(poolAbi, 'SupplyIncentiveClaimed')
const BORROW_INCENTIVE_CLAIMED = ev(poolAbi, 'BorrowIncentiveClaimed')
// Was HorizonClaimed, the LP farm's own claim event. That farm is gone; the emissions contract's
// remaining payout path is the vault one, and it carries the same `amount` field this hook sums.
const VAULT_INCENTIVE_CLAIMED = ev(farmAbi, 'VaultIncentiveClaimed')

/// Real historical series for the Stats page charts - TVL and cumulative HORIZON paid out (both
/// reserves' supply/borrow incentives, plus vault-share emissions) - all reconstructed from event logs
/// client-side, the same "subgraph done by hand" approach `useAllBorrowers` already uses for the
/// Liquidations page — and, since CHANGELOG.md item 102, through the same `getLogsChunked` helper,
/// so a real deployment's history no longer exceeds what a single `eth_getLogs` range may cover.
/// The remaining scaling caveat is cost, not correctness: this issues more windows as history
/// grows, and a real indexer is still the right answer once that becomes the bottleneck.
///
/// No fabricated or interpolated points - a fresh deployment with little history just produces a
/// short, honest line, not a padded fake one. The one synthetic point this adds on purpose is a
/// (deployBlockTimestamp, 0) anchor at the start of every series: before the first event, the
/// cumulative value truly was zero, so this states a real fact rather than inventing one.
///
/// TVL/borrows are tracked per-reserve in native units (`tvlUsdx`/`tvlCollateralEth`,
/// `borrowedUsdx`/`borrowedCollateralEth`) rather than combined into one USD figure - this app
/// has no historical price feed reconstruction (same "approx." caveat used elsewhere for APY/
/// market cap), so combining a native-ETH series with a USDx series into one number would need a
/// per-point historical price this hook doesn't have. Callers multiply the ETH-denominated
/// series by whatever CURRENT price they have, same as before.
///
/// No `cumYield` series any more - the retired `EarnPool.crankYield()` emitted an explicit
/// `YieldCranked` event per crank that this hook could sum; `HorizonLendingPool`'s interest accrual
/// is embedded directly in each reserve's `supplyIndex` growth with no equivalent discrete event
/// to reconstruct a "yield paid" series from. Dropped rather than approximated - no event, no
/// series, consistent with this hook's own "no fabricated data" rule.
export function useHistoricalStats() {
  const client = usePublicClient()
  const [series, setSeries] = useState(null) // null while loading, [] if nothing/failed
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!IS_LIVE || !client) { setLoading(false); return }
    let cancelled = false

    async function run() {
      setLoading(true)
      try {
        const fromBlock = ADDRESSES.LENDING_POOL_DEPLOY_BLOCK ? BigInt(ADDRESSES.LENDING_POOL_DEPLOY_BLOCK) : 0n
        // `getLogsChunked`, not a raw `fromBlock -> 'latest'` scan. One wide scan is fine against a
        // days-old dev chain and fails outright against a real deployment: most RPC providers cap a
        // single `eth_getLogs` range (commonly 10k-50k blocks) and reject anything wider, so these
        // seven calls would each start returning an error rather than data - and this hook's own
        // failure mode is a silently empty `/stats` page, which looks like "no activity" rather
        // than "the query broke". The helper resolves `latest` once and walks the range in windows.
        // `useAllBorrowers` and `useUserPortfolioHistory` already did this; these were the last
        // unwindowed scans left.
        const [supplied, withdrawn, borrowed, repaid, supplyIncentive, borrowIncentive, vaultIncentive] = await Promise.all([
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: SUPPLIED, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: WITHDRAWN, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: BORROWED, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: REPAID, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: SUPPLY_INCENTIVE_CLAIMED, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.LENDING_POOL, event: BORROW_INCENTIVE_CLAIMED, fromBlock }),
          getLogsChunked(client, { address: ADDRESSES.HORIZON_EMISSIONS, event: VAULT_INCENTIVE_CLAIMED, fromBlock }),
        ])

        const isEth = (l) => l.args.asset?.toLowerCase() === ADDRESSES.WETH.toLowerCase()

        const points = []
        for (const l of supplied) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { tvlCollateralEth: amt } : { tvlUsdx: amt }) })
        }
        for (const l of withdrawn) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { tvlCollateralEth: -amt } : { tvlUsdx: -amt }) })
        }
        // Net BORROW PRINCIPAL only (Borrowed - Repaid) - doesn't include interest accrual between
        // touches, same "real events, not a live-projected number" honesty tradeoff as the rest of
        // this hook. debtOf/reserveState (the live, interest-inclusive figures) are what the
        // reserve panels' own "Total borrowed" stat already shows for the up-to-date number.
        for (const l of borrowed) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { borrowedCollateralEth: amt } : { borrowedUsdx: amt }) })
        }
        for (const l of repaid) {
          const amt = Number(l.args.amount) / 1e18
          points.push({ block: l.blockNumber, idx: l.logIndex, ...(isEth(l) ? { borrowedCollateralEth: -amt } : { borrowedUsdx: -amt }) })
        }
        for (const l of supplyIncentive) points.push({ block: l.blockNumber, idx: l.logIndex, cumHorizonPaid: Number(l.args.amount) / 1e18 })
        for (const l of borrowIncentive) points.push({ block: l.blockNumber, idx: l.logIndex, cumHorizonPaid: Number(l.args.amount) / 1e18 })
        for (const l of vaultIncentive) points.push({ block: l.blockNumber, idx: l.logIndex, cumHorizonPaid: Number(l.args.amount) / 1e18 })

        points.sort((a, b) => (a.block === b.block ? a.idx - b.idx : Number(a.block - b.block)))

        // Resolve each unique block to a real timestamp - one getBlock per unique block, not per
        // event, so this stays cheap even with many events landing in the same block. The deploy
        // block itself is included so the series has a real (not guessed) zero-point anchor.
        const uniqueBlocks = [...new Set(points.map((p) => p.block))]
        if (!uniqueBlocks.some((b) => b === fromBlock)) uniqueBlocks.push(fromBlock)
        const blockTimes = new Map()
        await Promise.all(uniqueBlocks.map(async (bn) => {
          const block = await client.getBlock({ blockNumber: bn })
          blockTimes.set(bn, Number(block.timestamp) * 1000)
        }))

        let tvlUsdx = 0, tvlCollateralEth = 0, borrowedUsdx = 0, borrowedCollateralEth = 0, cumHorizonPaid = 0
        const out = [{ t: blockTimes.get(fromBlock), tvlUsdx, tvlCollateralEth, borrowedUsdx, borrowedCollateralEth, cumHorizonPaid }]
        for (const p of points) {
          tvlUsdx += p.tvlUsdx ?? 0
          tvlCollateralEth += p.tvlCollateralEth ?? 0
          borrowedUsdx += p.borrowedUsdx ?? 0
          borrowedCollateralEth += p.borrowedCollateralEth ?? 0
          cumHorizonPaid += p.cumHorizonPaid ?? 0
          out.push({ t: blockTimes.get(p.block), tvlUsdx, tvlCollateralEth, borrowedUsdx, borrowedCollateralEth, cumHorizonPaid })
        }

        if (!cancelled) setSeries(out)
      } catch (e) {
        console.error('useHistoricalStats: getLogs scan failed', e)
        if (!cancelled) setSeries([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [client])

  return { series, loading }
}
